import React, {
  Fragment,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import { CHANGEABLE_CATEGORIES } from 'constants/outfits';

import { unequipQuality } from 'actions/myself';
import { useQuality as _useQuality } from 'actions/storylet';

import withCurrentlyInStorylet from '../withCurrentlyInStorylet';

import EquippedItemComponent from './EquippedItemComponent';
import { ITooltipData } from 'components/ModalTooltip/types';
import { IAppState } from 'types/app';
import UseOrEquipModal from 'components/Possessions/components/UseOrEquipModal';

interface OwnProps {
  category: string,
  currentlyInStorylet: boolean,
  description: string,
  id: number,
  image: string,
  level: number,
  name: string,
  onOpenQualityPicker: (category: string) => void,
  shortlist?: boolean,
  useEventId?: number,
}

type StateProps = ReturnType<typeof mapStateToProps> & {
  dispatch: Function,
}

type Props = StateProps & OwnProps & RouteComponentProps;

function EquippedItemContainer(props: Props) {
  const {
    category,
    currentlyInStorylet,
    description,
    dispatch,
    history,
    id,
    image,
    isChanging,
    itemsUsableHere,
    level,
    name,
    onOpenQualityPicker,
    shortlist,
    useEventId,
  } = props;

  const canPlayerUseItems = itemsUsableHere && !currentlyInStorylet;
  const hasUseEventId = !!useEventId;

  const isChangeable = useMemo(() => CHANGEABLE_CATEGORIES.indexOf(category) >= 0, [category]);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleChange = useCallback(() => onOpenQualityPicker(category), [category, onOpenQualityPicker]);

  const handleUnequip = useCallback(() => {
    // Unchangeable items can't be unequipped through the UI
    if (!isChangeable) {
      return;
    }
    dispatch(unequipQuality(id));
  }, [dispatch, id, isChangeable]);

  const handleUse = useCallback(() => dispatch(_useQuality(id, history)), [dispatch, history, id]);

  const handleClick = useCallback(() => {
    if (!hasUseEventId) {
      return handleUnequip();
    }
    if (!canPlayerUseItems) {
      return handleUnequip();
    }
    setIsModalOpen(true);
  }, [
    canPlayerUseItems,
    handleUnequip,
    hasUseEventId,
  ]);

  const smallButtons = useMemo(() => {
    if (!isChangeable) {
      return [];
    }

    const smallButtons = [];
    smallButtons.push({ label: 'Unequip', action: handleUnequip });
    if (shortlist) {
      smallButtons.push({ label: 'Change', action: handleChange });
    }

    return smallButtons;
  }, [
    handleChange,
    handleUnequip,
    isChangeable,
    shortlist,
  ]);

  const tooltipData = useMemo(() => {
    let data: ITooltipData = {
      image,
      name,
      level,
      description,
      smallButtons,
    };

    if (hasUseEventId && canPlayerUseItems) {
      data.smallButtons = [
        ...(smallButtons ?? []),
        { label: 'Use', action: handleUse },
      ];
    }

    return data;
  }, [
    canPlayerUseItems,
    description,
    handleUse,
    hasUseEventId,
    image,
    level,
    name,
    smallButtons,
  ]);

  return (
    <Fragment>
      <EquippedItemComponent
        changeable={isChangeable}
        hasUseEventId={hasUseEventId}
        id={id}
        image={image}
        isChanging={isChanging}
        name={name}
        onClick={handleClick}
        shortlist={shortlist ?? false}
        tooltipData={tooltipData}
        tooltipTimeout={hasUseEventId && 500}
      />
      <UseOrEquipModal
        isOpen={isModalOpen}
        currentlyInStorylet={currentlyInStorylet}
        onRequestClose={() => setIsModalOpen(false)}
        onUse={handleUse}
        onEquip={handleUnequip}
        quality={{ image, name }}
        equipped
      />
    </Fragment>
  );
}

const mapStateToProps = ({
  outfit: { isChanging },
  map: { itemsUsableHere },
}: IAppState) => ({
  isChanging,
  itemsUsableHere,
});

export default withRouter(withCurrentlyInStorylet(connect(mapStateToProps)(EquippedItemContainer)));